import { useCallback, useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Copy, Link2, Wallet, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import SharedNavigation from "@/components/SharedNavigation";
import StripeConnectCard from "@/components/StripeConnectCard";
import { AffiliateStats } from "@/components/affiliate/AffiliateStats";
import { CommissionList, PayoutList } from "@/components/affiliate/AffiliateHistory";
import { usd, StatusPill } from "@/components/affiliate/affiliateFormat";

const ApplyCard = ({ settings, onApply, busy }) => (
  <Card className="bg-gradient-to-br from-purple-900/40 to-black border border-purple-500/30 p-6" data-testid="affiliate-apply-card">
    <h2 className="text-xl font-black text-white">Join the Founding Affiliate Program</h2>
    <p className="text-gray-300 text-sm mt-2 max-w-xl">
      Earn {Math.round(settings.commission_rate * 100)}% of the net first payment when someone you refer buys a paid membership.
      Founding affiliates are approved by the RGM team.
    </p>
    <p className="text-amber-200 text-sm mt-2 font-semibold" data-testid="affiliate-approval-notice">
      Your referrals only earn commissions after your affiliate account is approved. Memberships bought before approval do not count.
    </p>
    <ul className="text-xs text-gray-400 mt-3 space-y-1">
      <li>· Referral credit locks when they sign up ({settings.attribution_days}-day link window)</li>
      <li>· Commissions are held {settings.hold_days} days, then become available</li>
      <li>· Minimum payout {usd(settings.min_payout_cents)} · no commissions on renewals or upgrades</li>
    </ul>
    <Button onClick={onApply} disabled={busy || !settings.program_enabled} className="mt-5 bg-purple-600 hover:bg-purple-700" data-testid="affiliate-apply-btn">
      {settings.program_enabled ? "Apply to become an affiliate" : "Program not open yet"}
    </Button>
  </Card>
);

const LinkCard = ({ link }) => {
  const copy = () => { navigator.clipboard?.writeText(link); toast.success("Referral link copied"); };
  return (
    <Card className="bg-black/40 border border-white/10 p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="affiliate-link-card">
      <Link2 className="w-5 h-5 text-purple-300 shrink-0" />
      <code className="text-sm text-purple-100 break-all flex-1" data-testid="affiliate-referral-link">{link || "…"}</code>
      <Button size="sm" variant="outline" onClick={copy} disabled={!link} data-testid="affiliate-copy-link-btn"><Copy className="w-4 h-4 mr-1" />Copy</Button>
    </Card>
  );
};

const PayoutCard = ({ data, onRequest, busy }) => {
  const { stats, settings, stripe_connect_ready: ready, payouts } = data;
  const open = payouts.some((p) => ["requested", "approved", "sending"].includes(p.status));
  const enough = stats.available_cents >= settings.min_payout_cents;
  return (
    <Card className="bg-black/40 border border-emerald-500/20 p-5 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="affiliate-payout-card">
      <Wallet className="w-6 h-6 text-emerald-300 shrink-0" />
      <div className="flex-1 text-sm">
        <p className="text-white font-bold">Request a payout</p>
        <p className="text-gray-400 text-xs mt-1">
          Every payout is reviewed and approved by an admin. {ready ? "Your Stripe payout account is connected." : "Connect your payout account below (the same one artists use), or the team will pay you manually."}
        </p>
      </div>
      <Button onClick={onRequest} disabled={busy || open || !enough} className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-100 disabled:bg-emerald-950 disabled:text-emerald-200 disabled:border disabled:border-emerald-700" data-testid="affiliate-request-payout-btn">
        {open ? "Request in review" : enough ? `Request ${usd(stats.available_cents)}` : `Min ${usd(settings.min_payout_cents)}`}
      </Button>
    </Card>
  );
};

export default function AffiliateDashboardPage() {
  const [data, setData] = useState(null);
  const [link, setLink] = useState("");
  const [busy, setBusy] = useState(false);
  const [me, setMe] = useState(null);

  const load = useCallback(async () => {
    try {
      const [me, code] = await Promise.all([api.get("/affiliate/me"), api.get("/referral/my-code")]);
      setData(me.data);
      setLink(code.data.referral_link);
    } catch (e) {
      toast.error(e?.response?.status === 401 ? "Please sign in to view your affiliate dashboard" : "Could not load affiliate dashboard");
    }
  }, []);
  useEffect(() => { load(); api.get("/auth/me").then((r) => setMe(r.data)).catch(() => {}); }, [load]);

  const act = async (fn, ok) => {
    setBusy(true);
    try { await fn(); toast.success(ok); await load(); } catch (e) { toast.error(e?.response?.data?.detail || "Something went wrong"); }
    setBusy(false);
  };

  const aff = data?.affiliate;
  return (
    <div className="min-h-screen bg-[#08080c]">
      <Helmet><title>Affiliate Program | RGM Studio</title></Helmet>
      <SharedNavigation currentUser={me} activePage="referrals" />
      <div className="max-w-5xl mx-auto pt-24 pb-20 px-4 sm:px-6 space-y-5" data-testid="affiliate-dashboard">
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="text-4xl sm:text-5xl font-black text-white tracking-tight">Affiliate Program</h1>
          {aff && <StatusPill status={aff.status} testId="affiliate-status" />}
        </div>
        <p className="text-gray-400 text-sm flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-emerald-300" />Affiliate earnings are separate from coins, gifts and marketplace sales.</p>
        {!data ? <p className="text-gray-500" data-testid="affiliate-loading">Loading…</p> : !aff ? (
          <ApplyCard settings={data.settings} busy={busy} onApply={() => act(() => api.post("/affiliate/apply"), "Application sent for review")} />
        ) : aff.status !== "approved" ? (
          <Card className="bg-black/40 border border-white/10 p-6 text-gray-300" data-testid="affiliate-not-approved">
            {aff.status === "pending" ? "Your application is being reviewed by the RGM team. We'll notify you once approved. Referrals only start earning commissions after approval — memberships bought before then do not count." : `Your affiliate account is ${aff.status}. Referrals do not earn commissions while your account is not approved. Contact support for help.`}
          </Card>
        ) : (
          <>
            <LinkCard link={link} />
            <AffiliateStats stats={data.stats} holdDays={data.settings.hold_days} />
            <PayoutCard data={data} busy={busy} onRequest={() => act(() => api.post("/affiliate/payouts/request"), "Payout request sent for admin approval")} />
            <StripeConnectCard variant="wallet" />
            <div className="grid lg:grid-cols-2 gap-4">
              <CommissionList rows={data.commissions || []} />
              <PayoutList rows={data.payouts || []} />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
